<script lang="ts">
	import { format } from 'svelte-i18n';
	import { Button, ButtonV2, Container, Icon, Modal, Text } from '@Shelly/shelly-design-system';
	import { capFirstChar } from 'src/helpers/utilities';
	import { push } from 'svelte-spa-router';
	import { MAX_AI_MODEL_LIMIT } from 'src/constants';
	import { setSidePanelState } from 'src/components/Sidepanel/sidepanel-helpers';
	import { showNewLayout } from 'src/stores/toDelete/new_layout';
	export let showAboutAIModelModal = false,
		device,
		isPremiumUser,
		numberOfAIModels;

	$: ButtonC = $showNewLayout ? ButtonV2 : Button;

	const listAIBenefits = [
		{
			icon: 'fal fa-chart-line-up icon-ai-color',
			title: $format('title_higher_consumption_detection', {
				default: 'Higher-Consumption Detection',
			}),
			text: $format('text_higher_consumption_detection', {
				default: 'Detects when your device consumes more energy than usual.',
			}),
		},
		{
			icon: 'fal fa-chart-line-down icon-ai-color',
			title: $format('title_under_consumption_detection', {
				default: 'Under-Consumption Detection',
			}),
			text: $format('text_under_consumption_detection', {
				default: 'Detects when your device consumes less energy than usual.',
			}),
		},
		{
			icon: 'fal fa-plug-circle-bolt icon-ai-color',
			title: $format('title_long_working_cycles', {
				default: 'Long Working Cycles',
			}),
			text: $format('text_long_working_cycles', {
				default:
					'Detects when your device runs longer than expected, indicating potential strain or inefficiency.',
			}),
		},
		{
			icon: 'fal fa-plug-circle-xmark icon-ai-color',
			title: $format('title_long_non_working_cycles', {
				default: 'Long Non-Working Cycles',
			}),
			text: $format('text_long_non_working_cycles', {
				default:
					'Detects when your device remains idle longer than expected, indicating inefficient usage patterns.',
			}),
		},
	];

	const goToPremiumSettings = () => {
		push('/settings/premium');

		setSidePanelState({
			type: 'device',
			id: device.id,
			open: false, // doesn't close the device drawer
		});
	};
</script>

<!-- About AI model modal -->
<Modal
	backdropClose={false}
	title={$format('settings_about_energy_ai_insights', {
		default: 'About Energy AI insights',
	})}
	on:close={() => {
		showAboutAIModelModal = false;
	}}
	showModal={showAboutAIModelModal}
>
	<Container slot="content" padding="0">
		<div class="d-flex align-items-center justify-content-center">
			<Text styleType="body" class="text-center" element="p">
				<img class="mb-m mt-m" src="./images/ai/ai-sparkles.svg" alt="ai-logo" />
			</Text>
		</div>
		<div>
			<div>
				<Text styleType="h1" class="text-center mt-m" element="h1">
					{$format('settings_headline_energy_ai_insights', {
						default: 'Spot energy issues with Energy AI insights',
					})}
				</Text>
				<Text styleType="body" class=" mt-m" element="p">
					{$format('settings_intro_energy_ai_insights', {
						default:
							'By training this device, you can spot issues and inefficiencies in how it uses energy and catch problems early.',
					})}
				</Text>
				<Text styleType="body" class="mt-m text-center" element="p">
					{@html $format('subtitle_you_have_numbers_of_ai_models', {
						values: {
							numberOfAIModels,
							MAX_AI_MODEL_LIMIT,
						},
						default: `Currently, you have trained <b>${numberOfAIModels} / ${MAX_AI_MODEL_LIMIT}</b> AI models, to protect your assets.`,
					})}
				</Text>
				<Text styleType="h2" class="text-center mt-m" element="h2">
					{$format('settings_designed_to_detect_energy_ai', {
						default: 'Energy AI insights is designed to detect:',
					})}
				</Text>
			</div>
			<div>
				<ul class="pl-0">
					{#each listAIBenefits as benefit, index}
						<li class="d-flex align-items-center">
							<Icon icon={benefit.icon} size="m" class="text-primary mr-s " />
							<div>
								<Text styleType="body-small" class="mb-xs" element="h3">
									{benefit.title}
								</Text>
								<Text styleType="body-extra-small" element="p" class="text-color-muted">
									{benefit.text}
								</Text>
							</div>
						</li>
						{#if index !== listAIBenefits.length - 1}
							<hr class="mt-s mb-s hr-color-2" />
						{/if}
					{/each}
				</ul>
			</div>
			<div>
				<Text styleType="body" class="mt-m" element="p">
					{@html $format('ai_is_finalized', {
						default:
							'Once the model is finalized, you will receive <b>automated notifications whenever any of these events occur</b>, helping you keep your device running smoothly.',
					})}
				</Text>
			</div>
			<div>
				<!-- if user is premium show "cancel" button else show "get premium" and "maybe later" buttons-->
				{#if isPremiumUser}
					<Text class="text-center mt-m">
						<svelte:component
							this={ButtonC}
							size="l"
							width="auto"
							on:click={() => {
								showAboutAIModelModal = false;
							}}
						>
							{capFirstChar(
								$format('okay_got_it', {
									default: 'Okay, got it.',
								}),
							)}
						</svelte:component>
					</Text>
				{:else}
					<Text class="text-center mt-m">
						<svelte:component
							this={ButtonC}
							variant="premium"
							width="auto"
							on:click={goToPremiumSettings}
						>
							{capFirstChar(
								$format('button_get_premium', {
									default: 'Get Premium',
								}),
							)}
						</svelte:component>
					</Text>
					<Text
						styleType="body"
						class="mt-m cursor-pointer text-primary text-center"
						on:click={() => {
							showAboutAIModelModal = false;
						}}
					>
						{$format('maybe_later', { default: 'Maybe later' })}
					</Text>
				{/if}
			</div>
		</div>
	</Container>
</Modal>
